<script setup lang="ts">
import { $message } from '@nio-fe/lego';
import humps from 'humps';
import { inject, ref, watch } from 'vue';
import copy from 'copy-to-clipboard';
import useGlobalStore from '@/store/globalStore';
import { useCouponStore } from '@/store/couponStore';
import { noticeMicro } from '@/utils/messageHandler';
import { asyncVueComponent } from '@nio-fe/mate-lib-loader';
import ContactSelectionDialog from '@/components/user/ContactSelectionDialog.vue';
import { BRAND_MAP } from '@/enums';
const TitContactSelectionDialog = asyncVueComponent(
  '@nio-wad/ladybird',
  '0',
  'TitContactSelectionDialog'
);
const TitCouponCard = asyncVueComponent(
  '@nio-wad/ladybird',
  '0',
  'TitCouponCard'
);
const contactDialogRef = ref<typeof TitContactSelectionDialog | null>(null);
const couponStore = useCouponStore();
const $sensors = inject<any>('$sensors');
const globalStore = useGlobalStore();
const dialogVisible = ref(false);
const describeValue = ref('');
const loading = ref(false);
const selectedCoupon = ref<any>(null);
const selectedUserAccountId = ref<any>(null);

watch(
  () => dialogVisible.value,
  () => {
    couponStore.setDialogOpenStatus(dialogVisible.value);
  }
);

const handleStartSendToMessage = () => {
  contactDialogRef.value?.showDialog();
};

const sendCoupon = (channel: string, accountId?: string) => {
  const selected = selectedCoupon.value;
  if (!selected) {
    return;
  }

  return couponStore
    .sendCoupon({
      issueId: selected.couponIssueId,
      message: describeValue.value,
      receiverAccountId: accountId,
      channel,
    })
    .catch((e) => {
      $message.error('转赠失败:' + e.message);
      throw e;
    });
};
const sendToMessage = async (accountId) => {
  return new Promise(async (resolve, reject) => {
    loading.value = true;
    let timeoutId: number = 0;
    try {
      const data = await sendCoupon('im', accountId);
      // @ts-ignore
      timeoutId = setTimeout(() => {
        $message.error({ message: '转赠失败' });
        loading.value = false;
        resolve(false);
        $sensors.track('CouponClick', {
          requirement_id: 522,
          button_name: '确定',
          track_sign: 'aplatform.b2443.d2446.click.CouponClick',
          coupon_id: selectedCoupon.value.couponIssueId,
          coupon_name: selectedCoupon.value.subTitle,
          failure_message: 'message notice timeout',
          page: '赠送卡券',
          mp_id: 'com.nio.titan',
        });
      }, 2 * 1000);
      const noticeMsg = {
        action: 'sendCoupon',
        shortLink: data.originalUrl,
        originalUrl: data.originalUrl,
        receiverAccountId: accountId,
        brand: couponStore.currentBrand,
        tips: describeValue.value || '',
        coupon: humps.decamelizeKeys({
          ...selectedCoupon.value,
        }),
      };
      noticeMicro('im', noticeMsg, () => {
        timeoutId > 0 && clearTimeout(timeoutId);
        loading.value = false;
        $sensors.track('CouponClick', {
          requirement_id: 522,
          button_name: '确定',
          track_sign: 'aplatform.b2443.d2446.click.CouponClick',
          coupon_id: selectedCoupon.value.couponIssueId,
          coupon_name: selectedCoupon.value.subTitle,
          is_success: true,
          page: '赠送卡券',
          mp_id: 'com.nio.titan',
        });
        $message.success({ message: '转赠成功' });
        closeDialog();
        resolve(true);
      });
    } catch (e: any) {
      timeoutId > 0 && clearTimeout(timeoutId);
      $message.error({ message: '转赠失败' });
      $sensors.track('CouponClick', {
        requirement_id: 522,
        button_name: '确定',
        track_sign: 'aplatform.b2443.d2446.click.CouponClick',
        coupon_id: selectedCoupon.value.couponIssueId,
        coupon_name: selectedCoupon.value.subTitle,
        failure_message: e.displyMsg || e.message,
        page: '赠送卡券',
        mp_id: 'com.nio.titan',
      });
      resolve(false);
      loading.value = false;
    }
  });
};
const handleBeforeSubmit = (contact) => {
  if (!contact?.selected?.length) {
    return;
  }
  return sendToMessage(contact.selected[0].accountId);
};

const handleSendToWechat = async () => {
  try {
    if (!selectedCoupon.value) {
      return;
    }
    loading.value = true;
    const data = await sendCoupon('wechat');
    $message.success({ message: '复制成功，前往微信粘贴' });
    const sentContent = `【${
      BRAND_MAP[couponStore.currentBrand] || BRAND_MAP.nio
    }】送你一份心意
[卡券] ${selectedCoupon.value.couponName}
${describeValue.value}
点击领取：${data.shortUrl}`;
    copy(sentContent);
  } catch (e) {
    $message.error({ message: '转赠失败' });
  } finally {
    loading.value = false;
  }
};
const showDialog = ({ selected, accountId }) => {
  selectedCoupon.value = selected;
  selectedUserAccountId.value = accountId;
  dialogVisible.value = true;
  loading.value = false;
  describeValue.value = '';
};
const closeDialog = () => {
  selectedCoupon.value = null;
  dialogVisible.value = false;
};
defineExpose({ showDialog, closeDialog });
</script>

<template>
  <n-dialog
    class="send-card-confirm-dialog"
    :z-index="1000"
    size="small"
    display-type="if"
    v-model="dialogVisible"
    title="送上一份心意"
  >
    <tit-coupon-card
      :coupon="selectedCoupon"
      v-if="selectedCoupon"
    ></tit-coupon-card>
    <n-form class="send-card-confirm-dialog__form-wrapper">
      <n-form-item label="写心意">
        <n-input
          v-model="describeValue"
          type="textarea"
          :maxlength="50"
          :rows="3"
          show-limit
          placeholder="请在这里写上你的心意吧～"
        />
      </n-form-item>
    </n-form>

    <template #footer>
      <div class="send-card-confirm-dialog__dialog-footer">
        <n-button
          size="small"
          type="secondary"
          kind="positive"
          :disabled="loading"
          @click="handleSendToWechat"
          v-tit-tracker:click="{
            trackName: 'CouponClick',
            requirement_id: 522,
            track_sign: 'aplatform.b2443.d2446.click.CouponClick',
            coupon_name: selectedCoupon?.subTitle,
            button_name: '复制到微信',
            coupon_id: selectedCoupon?.couponIssueId,
            page: '赠送卡券',
          }"
          >复制到微信</n-button
        >
        <n-button
          style="margin-left: 16px"
          size="small"
          type="secondary"
          kind="positive"
          :disabled="loading"
          @click="handleStartSendToMessage"
          v-tit-tracker:click="{
            trackName: 'CouponClick',
            requirement_id: 522,
            track_sign: 'aplatform.b2443.d2446.click.CouponClick',
            coupon_name: selectedCoupon?.subTitle,
            button_name: '发送到消息',
            coupon_id: selectedCoupon?.couponIssueId,
            page: '赠送卡券',
          }"
          >发送到消息</n-button
        >
      </div>
    </template>
  </n-dialog>

  <contact-selection-dialog
    :before-submit="handleBeforeSubmit"
    ref="contactDialogRef"
  />
</template>

<style lang="scss">
.send-card-confirm-dialog__form-wrapper {
  margin-top: 24px;
}
.send-card-confirm-dialog__dialog-footer {
  text-align: right;
}
.send-card-confirm-dialog {
  z-index: 2001 !important;
  .n-dialog__mask {
    z-index: 2001 !important;
  }
  .n-dialog__container {
    z-index: 2002 !important;
  }
}
</style>
